import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEditorState } from "@/store";

// Time to keep the overlay visible after loading resolves, before it starts
// fading out — avoids a flash-hide on very fast navigations. Skipped for QA
// subcircles, which should disappear immediately.
const HIDE_DELAY_MS = 1000;

// Always mounted, crossfaded via opacity only (compositor-driven) so it
// reliably shows/hides even if the main thread stalls during a Godot scene
// transition — same technique as .sub-step-node-loading-overlay in index.css.
const LayoutLoadingOverlay = () => {
  const { t } = useTranslation();
  const circleFlow = useEditorState((state) => state.circleFlow);
  const pendingNavigateIndex = useEditorState((state) => state.circleFlowPendingIndex);
  const groupNavigatePending = useEditorState((state) => state.circleFlowGroupNavigatePending);

  const flatSubcircles = circleFlow?.flatMap((group) => group.subcircles) ?? [];
  const loadingSub =
    flatSubcircles.find((sub) => sub.index === pendingNavigateIndex) ??
    flatSubcircles.find((sub) => sub.loading) ??
    null;

  const isLoading =
    pendingNavigateIndex !== null ||
    groupNavigatePending ||
    !!circleFlow?.some((group) => group.subcircles.some((sub) => sub.loading));

  // Remembers the icon of whichever subcircle is driving the loading state,
  // so it's still available once isLoading flips back to false (by then
  // loadingSub itself has already gone null).
  const loadingIconRef = useRef<string | undefined>(undefined);
  if (loadingSub?.icon) {
    loadingIconRef.current = loadingSub.icon.toLowerCase();
  }

  const [visible, setVisible] = useState(isLoading);

  useEffect(() => {
    if (isLoading) {
      setVisible(true);
      return;
    }
    if (loadingIconRef.current === "qa") {
      setVisible(false);
      return;
    }
    const timeout = setTimeout(() => setVisible(false), HIDE_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [isLoading]);

  return (
    <div
      className={`absolute inset-0 z-1000 flex flex-col items-center justify-center gap-8 transition-opacity duration-200 ease-out ${visible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.35)",
        backdropFilter: "blur(11px)",
      }}
    >
      <h1 className="text-4xl  text-gray-800 font-alata">{t("layoutLoading")}</h1>
      <div className="flex">
        <div className="dot dot--one" style={{ width: 22, height: 22 }} />
        <div className="dot dot--two" style={{ width: 22, height: 22 }} />
        <div className="dot dot--three" style={{ width: 22, height: 22 }} />
      </div>
    </div>
  );
};

export default LayoutLoadingOverlay;
